import router from "@cms/core/plugin/router";
import { queryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { revokeAccessToken } from "@verbleif/shared/src/composables/Api/Plugin/Auth/revokeAccessToken";
import { resetMyAppStoreAccess } from "@verbleif/shared/src/composables/AppStore/useMyAppStoreAccess";
import { revokeCurrentSession } from "@verbleif/shared/src/composables/AuthStore/revokeCurrentSession";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useConfigStore } from "@verbleif/shared/src/composables/ConfigStore/useConfigStore";
import { useLocationStore } from "@verbleif/shared/src/composables/LocationStore/useLocationStore";
import { useSharedStore } from "@verbleif/shared/src/composables/SharedStore/useSharedStore";

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function useLogout() {
  const authStore = useAuthStore();
  const { config } = useConfigStore();
  const locationStore = useLocationStore();
  const sharedStore = useSharedStore();

  async function logout({
    redirectToLogin = true,
    keepReturnUrl = false,
    logoutAuthServer = true,
  }: {
    redirectToLogin?: boolean
    keepReturnUrl?: boolean
    logoutAuthServer?: boolean
  } = {}) {
    if (!config.value) {
      throw new Error("Config not set");
    }

    await router.push({ name: "logout" });

    // Revoked while the logout screen shows, before the stores below drop the token both calls need.
    // The session revoke goes before the access token revoke, which would otherwise reject its bearer.
    await Promise.all([
      redirectToLogin ? revokeCurrentSession().then(revokeAccessToken) : Promise.resolve(),
      delay(1000),
    ]);

    if (redirectToLogin) {
      await Promise.all([
        authStore.reset(keepReturnUrl),
        locationStore.reset(),
        sharedStore.reset(),
        queryClient.clear(),
        resetMyAppStoreAccess(),
      ]);

      if (import.meta.env.VITE_SKIP_OAUTH === "true") {
        await router.push({ name: "login" });
      } else {
        console.log("[Logout] Redirecting to login", config.value.loginUrl);
        if (logoutAuthServer) {
          window.location.replace(`${config.value.loginUrl}/logout`);
        } else {
          window.location.replace(config.value.loginUrl);
        }
      }
    }
  }

  return { logout };
}
